/* Rebuy PDP recommendations: fetches recommendations and renders cards into each placeholder.
   Add controls must keep .js-rebuy-add (or .js-quick-add on the carousel) and the data-rebuy-*
   attributes, or attribution silently drops. */

(function () {
  /* Interactive layouts. The carousel layout is driven by <related-products-carousel>;
     these three own their own state because they are not scroll-based. */
  function initLayoutBehaviour(root, layout, moneyFormat, svc, t) {
    if (layout === 'list') {
      const more = root.querySelector('.rebuy-list__more');
      const panel = root.querySelector('.rebuy-list__extra');
      if (!more || !panel) return;
      more.dataset.showLabel = more.textContent;
      more.addEventListener('click', function () {
        const open = more.getAttribute('aria-expanded') === 'true';
        panel.classList.toggle('is-open', !open);
        more.setAttribute('aria-expanded', String(!open));
        more.textContent = open ? more.dataset.showLabel : t('showFewer');
      });
    }

    if (layout === 'bundle') {
      const checks = [...root.querySelectorAll('.rebuy-bundle__check')];
      const totalEl = root.querySelector('[data-rebuy-bundle-total]');
      const countEl = root.querySelector('.rebuy-bundle__count');
      const cta = root.querySelector('.rebuy-bundle__cta');
      if (!checks.length || !cta) return;

      const sync = function () {
        const chosen = checks.filter(function (check) { return check.checked; });
        const pence = chosen.reduce(function (sum, check) { return sum + (parseFloat(check.dataset.price) || 0) * 100; }, 0);
        const money = svc.formatMoney(pence, moneyFormat);
        totalEl.textContent = money;
        countEl.textContent = chosen.length === 1 ? t('bundleCountOne') : t('bundleCountOther', { count: chosen.length });
        cta.disabled = chosen.length === 0;
        // Changing the selection makes a previous add stale, so drop the confirmed state with it.
        cta.classList.remove('is-added');
        // Figma puts the running total on the CTA itself.
        cta.textContent = chosen.length ? t('addToCart') + ' \u00b7 ' + money : t('addToCart');
      };
      checks.forEach(function (check) { check.addEventListener('change', sync); });
      sync();

      cta.addEventListener('click', async function () {
        const chosen = checks.filter(function (check) { return check.checked; });
        if (!chosen.length) return;
        cta.disabled = true;
        const original = cta.textContent;
        cta.textContent = t('adding');
        try {
          for (const check of chosen) {
            svc.registerPendingAttribution(check.dataset.rebuyProductId, check.dataset.rebuySku, check.dataset.rebuyAttribution);
            // Use the block's configured attribution id, not a literal, so Rebuy's reporting can
            // tell this placement apart from the others. There is no A/B test to fall back on.
            await svc.addToCart(check.dataset.rebuySku, 1, check.dataset.rebuyAttribution || 'rebuy-bundle');
          }
          cta.textContent = t('added') + ' \u2713';
          cta.classList.add('is-added');
        } catch (err) {
          cta.textContent = original;
          cta.disabled = false;
        }
      });
    }

    if (layout === 'single') {
      root.querySelectorAll('[data-rebuy-variant-select]').forEach(function (sel) {
        sel.addEventListener('change', function () {
          const opt = sel.options[sel.selectedIndex];
          const cta = sel.closest('.rebuy-single__slide').querySelector('.js-rebuy-add, .js-quick-add');
          // Attribution is keyed on the sku, so it has to follow the selection.
          if (cta && opt) cta.dataset.rebuySku = opt.dataset.sku || '';
        });
      });

      const slides = [...root.querySelectorAll('.rebuy-single__slide')];
      const dots = [...root.querySelectorAll('.rebuy-single__dot')];
      const countEl = root.querySelector('.rebuy-single__count');
      if (!slides.length) return;
      let current = 0;
      const show = function (next) {
        current = (next + slides.length) % slides.length;
        slides.forEach(function (slide, slideIndex) { slide.hidden = slideIndex !== current; });
        dots.forEach(function (dot, dotIndex) { dot.setAttribute('aria-selected', String(dotIndex === current)); });
        if (countEl) countEl.textContent = t('slideCounter', { current: current + 1, total: slides.length });
      };
      root.querySelectorAll('.rebuy-single__arrow').forEach(function (btn) {
        btn.addEventListener('click', function () { show(current + parseInt(btn.dataset.dir, 10)); });
      });
      dots.forEach(function (dot) {
        dot.addEventListener('click', function () { show(parseInt(dot.dataset.index, 10)); });
      });
    }

    if (layout === 'switcher') {
      const strip = root.querySelector('.rebuy-switcher__tabs');
      const marker = root.querySelector('.rebuy-switcher__marker');
      const tabs = [...root.querySelectorAll('.rebuy-switcher__tab')];
      const panels = [...root.querySelectorAll('.rebuy-switcher__panel')];
      if (!tabs.length) return;

      // Measured from live rects rather than offsetLeft, so border and padding cannot skew it.
      const moveMarker = function (index) {
        if (!marker || !strip) return;
        const sr = strip.getBoundingClientRect();
        const tr = tabs[index].getBoundingClientRect();
        if (!tr.width) return;
        marker.style.width = tr.width + 'px';
        // Vertical position is CSS (top/bottom pinned). Only the horizontal is measured, and the
        // strip's border comes off because the marker is placed against its padding box.
        marker.style.transform = 'translateX(' + (tr.left - sr.left - strip.clientLeft) + 'px)';
      };

      const select = function (index) {
        tabs.forEach(function (tab, tabIndex) { tab.setAttribute('aria-selected', String(tabIndex === index)); });
        panels.forEach(function (panel, panelIndex) { panel.hidden = panelIndex !== index; });
        moveMarker(index);
      };

      // Place it before enabling the transition, so it does not slide in from the corner.
      moveMarker(0);
      requestAnimationFrame(function () { strip.classList.add('rebuy-switcher__tabs--ready'); });
      window.addEventListener('resize', function () {
        const active = tabs.findIndex(function (tab) { return tab.getAttribute('aria-selected') === 'true'; });
        moveMarker(active < 0 ? 0 : active);
      });
      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () { select(parseInt(tab.dataset.index, 10)); });
        tab.addEventListener('keydown', function (event) {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          event.preventDefault();
          const next = (parseInt(tab.dataset.index, 10) + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
          tabs[next].focus();
          select(next);
        });
      });
    }
  }

  /* Direct add for single-variant accessories. Mirrors the bundle CTA: attribution is registered
     before the cart call, or Rebuy reporting drops it. */
  function bindDirectAdd(root, svc, t) {
    root.addEventListener('click', async function (event) {
      const btn = event.target.closest('.js-rebuy-add');
      if (!btn || btn.disabled) return;
      event.preventDefault();
      const original = btn.textContent;
      btn.disabled = true;
      btn.textContent = t('adding');
      try {
        svc.registerPendingAttribution(btn.dataset.rebuyProductId, btn.dataset.rebuySku, btn.dataset.rebuyAttribution);
        await svc.addToCart(btn.dataset.rebuySku, 1, btn.dataset.rebuyAttribution || 'rebuy-pdp');
        btn.textContent = t('added') + ' \u2713';
        btn.classList.add('is-added');
      } catch (err) {
        btn.textContent = original;
        btn.disabled = false;
      }
    });
  }

  function initRebuyRecommendations(carouselElement) {
    const placeholder = carouselElement.querySelector('[data-rebuy-pdp-recommendations]');
    if (!placeholder || placeholder.dataset.rebuyInitialised === 'true') return;
    placeholder.dataset.rebuyInitialised = 'true';

    const productId = placeholder.dataset.productId;
    const limit = parseInt(placeholder.dataset.limit, 10) || 4;
    const buttonType = placeholder.dataset.buttonType || 'link';
    const buttonText = placeholder.dataset.buttonText || 'Shop Now';
    const showVendor = placeholder.dataset.showVendor !== 'false';
    const showRrp = placeholder.dataset.showRrp !== 'false';

    // All user-facing copy arrives from Liquid via | t. Guarded: a malformed payload must not blank
    // the interface, so every read falls back to a sensible English default.
    const STRING_FALLBACKS = {
      addToCart: 'Add to Cart', adding: 'Adding...', added: 'Added to Cart', quickAdd: 'Quick Add',
      listLede: '', bundleLede: '', bundleNote: '', bundleTotalLabel: '', bundleFoot: '',
      bundleCountOne: '1 accessory selected', bundleCountOther: '{{ count }} accessories selected',
      showMoreOne: '', showMoreOther: '', singleLede: '', slideLabel: 'Accessory {{ index }}',
      slidePrev: 'Previous accessory', slideNext: 'Next accessory', showFewer: 'Show fewer accessories',
      switcherTitle: '', switcherSub: '', switcherFoot: '', roles: [],
      slideCounter: '{{ current }} of {{ total }}', chooseOption: 'Choose an option', optionFallback: 'Option'
    };
    const strings = (function () {
      try {
        return Object.assign({}, STRING_FALLBACKS, JSON.parse(placeholder.dataset.strings || '{}'));
      } catch (err) {
        return STRING_FALLBACKS;
      }
    })();
    // Shopify's {{ count }} / {{ index }} placeholders, interpolated client side.
    const t = function (key, replacements) {
      let out = strings[key] || '';
      Object.keys(replacements || {}).forEach(function (name) {
        out = out.replace(new RegExp('\\{\\{\\s*' + name + '\\s*\\}\\}', 'g'), replacements[name]);
      });
      return out;
    };

    // Rebuy's payload has no metafields, and compare_at_price is null across this store, so RRP has
    // to come from Liquid. One small Section Rendering fetch per product, cached for the session.
    const rrpCache = {};
    const fetchRRP = async function (handle) {
      if (!handle) return '';
      if (handle in rrpCache) return rrpCache[handle];
      const stored = (function () {
        try { return sessionStorage.getItem('rebuy-rrp:' + handle); } catch (e) { return null; }
      })();
      if (stored !== null) { rrpCache[handle] = stored; return stored; }
      try {
        const res = await fetch(window.Shopify.routes.root + 'products/' + handle + '?section_id=rebuy-product-rrp');
        if (!res.ok) throw new Error(res.status);
        // The section renders a bare string; strip the Shopify section wrapper.
        const text = (await res.text()).replace(/<[^>]*>/g, '').trim();
        rrpCache[handle] = text;
        try { sessionStorage.setItem('rebuy-rrp:' + handle, text); } catch (e) {}
        return text;
      } catch (e) {
        rrpCache[handle] = '';
        return '';
      }
    };

    // Fill the RRP slots after render, so a slow or failed lookup never blocks the cards.
    const hydrateRRP = function (root) {
      root.querySelectorAll('[data-rebuy-rrp-for]').forEach(async function (el) {
        const rrp = await fetchRRP(el.dataset.rebuyRrpFor);
        if (!rrp || !el.isConnected) return;
        el.textContent = rrp;
        el.hidden = false;
      });
    };

    // Pricing rule, mirroring snippets/product-card.liquid:
    // trade price is SparkLayer and is for logged-in customers ONLY. On this store the Shopify
    // base price IS the trade price, so anything rendered from Shopify data would leak it too.
    // Logged out shows RRP or nothing at all.
    const viewerIsLoggedIn = function () {
      return !!(window.__rebuyCustomer && window.__rebuyCustomer.id);
    };
    const buyOnlineLabel = placeholder.dataset.buyOnlineLabel || 'View here';
    const attributionId = placeholder.dataset.attributionId || 'pdp-recommendations';
    const cardBackground = carouselElement.dataset.cardBackground || '#f4f4f4';
    const showCardButton = carouselElement.dataset.showCardButton !== 'false';
    const moneyFormat = placeholder.dataset.moneyFormat || '';
    const tradePriceFormat = placeholder.dataset.tradePriceFormat || '';
    const placeholderImage = placeholder.dataset.placeholderImage || '';
    const placeholderSvg = placeholder.dataset.placeholderSvg || '';
    const plainMoneyFormat = placeholder.dataset.plainMoneyFormat || '';
    // Logged-out visitors cannot add to cart on this store, so a layout built around adding
    // (the multi-select bundle especially) has nothing to do. The section can substitute a
    // browsing layout instead, or render nothing at all.
    const loggedOutLayout = placeholder.dataset.layoutLoggedOut || '';
    const viewerLoggedIn = !!(window.__rebuyCustomer && window.__rebuyCustomer.id);
    let layout = placeholder.dataset.layout || 'carousel';
    if (!viewerLoggedIn && loggedOutLayout) {
      if (loggedOutLayout === 'hidden') {
        const host = placeholder.closest('.rebuy-section') || placeholder.closest('related-products-carousel');
        if (host) host.remove();
        return;
      }
      layout = loggedOutLayout;
    }


  const handleIntersection = function(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(placeholder);

    function fetchWhenReady() {
      if (window.RebuyService && window.RebuyService.ready) {
        doFetch();
      } else if (window.RebuyService) {
        window.RebuyService.init().then(doFetch);
      } else {
        document.addEventListener('rebuy-service:ready', doFetch, { once: true });
        setTimeout(doFetch, 10000);
      }
    }

    let fetched = false;
    function doFetch() {
      if (fetched) return;
      fetched = true;
      const rebuyConfig = window.__rebuyConfig || {};
      const options = {
        productId: productId,
        limit: limit,
        context: 'pdp'
      };
      const blockDataSourceId = placeholder.dataset.dataSourceId;
      if (blockDataSourceId) {
        options.dataSourceId = blockDataSourceId;
      }

      const fetchTimeout = new Promise(function(_, reject) {
        setTimeout(function() { reject(new Error('timeout')); }, 10000);
      });

      Promise.race([window.RebuyService.getRecommendations(options), fetchTimeout])
        .then(function(products) {
          if (!products || !products.length) {
            carouselElement.remove();
            return;
          }

          const svc = window.RebuyService;
          const esc = svc.escapeHTML;

          const isLoggedIn = !!(window.__rebuyCustomer && window.__rebuyCustomer.id);

          // Attribution rides on the add class plus these three attributes. Strip them and
          // Rebuy attribution silently drops to zero, with nothing on screen to show it.
          const buildButton = function (product, data, extraClass) {
            const cls = 'btn btn--primary related_products__buy_buttons' + (extraClass ? ' ' + extraClass : '');
            if (isLoggedIn && buttonType === 'quick_add') {
              // Adds the exact SKU the card is showing, rather than routing through the quick-add
              // drawer. Rebuy's variants array does not agree with Shopify's, so gating on its
              // length sent single-variant products to a drawer with nothing to choose.
              return '<button type="button" class="' + cls + ' js-rebuy-add no-js-hidden" data-product-url="' + data.productUrl + '" data-rebuy-attribution="' + esc(attributionId) + '" data-rebuy-product-id="' + product.id + '" data-rebuy-sku="' + esc(data.sku) + '">' + esc(t('addToCart')) + '</button>';
            }
            const label = isLoggedIn ? buttonText : buyOnlineLabel;
            return '<a href="' + data.productUrl + '" class="' + cls + '">' + esc(label) + '</a>';
          };

          const buildMedia = function (product, data, width, cls) {
            let inner;
            if (data.primaryImage) {
              inner = '<img src="' + svc.getImageUrl(data.primaryImage, width) + '" alt="' + esc(product.title || '') + '" loading="lazy">';
            } else if (placeholderImage) {
              inner = '<img src="' + placeholderImage + '" alt="" loading="lazy">';
            } else {
              inner = placeholderSvg;
            }
            return '<span class="' + (cls || 'rebuy-media') + '">' + inner + '</span>';
          };

          const tradePrice = function (product) {
            // Trade price is SparkLayer's and is for logged-in customers only. On this store the
            // Shopify base price IS the trade price, so it must never render to a logged-out visitor.
            if (!viewerLoggedIn) return '';
            return '<sparklayer-pricing product-id="' + product.id + '" data-string="" data-format="' +
              esc(tradePriceFormat) + '"></sparklayer-pricing>';
          };
          const rrpPrice = function (data) {
            // Mirrors snippets/product-card.liquid: RRP shows when the setting says so OR when the
            // viewer is logged out. It is the only price a logged-out visitor may see.
            if (!showRrp && viewerLoggedIn) return '';
            const handle = (data.productUrl || '').split('/products/')[1];
            if (!handle) return '';
            return '<span class="related_product__rrp" data-rebuy-rrp-for="' +
              esc(handle.split('?')[0]) + '" hidden></span>';
          };

          const cardsHTML = products.map(function(product) {
            const data = svc.extractProductData(product);
            let primaryImageHtml;
            if (data.primaryImage) {
              primaryImageHtml = '<img src="' + svc.getImageUrl(data.primaryImage, 200) + '" alt="' + esc(product.title || '') + '" class="primary-image" loading="lazy" width="200" height="200">';
            } else if (placeholderImage) {
              primaryImageHtml = '<img src="' + placeholderImage + '" alt="" class="primary-image" loading="lazy" width="200" height="200">';
            } else {
              primaryImageHtml = placeholderSvg;
            }
            let hoverSrc = '';
            if (product.images && product.images.length > 1 && product.images[1].src) {
              hoverSrc = svc.getImageUrl(product.images[1].src, 200);
            }

            const alt = esc(product.title || '');

            const isLoggedIn = !!(window.__rebuyCustomer && window.__rebuyCustomer.id);

            let buttonHtml = '';
            if (isLoggedIn && buttonType === 'quick_add') {
              buttonHtml = '<button class="btn btn--primary related_products__buy_buttons js-quick-add no-js-hidden" data-product-url="' + data.productUrl + '" data-rebuy-attribution="' + esc(attributionId) + '" data-rebuy-product-id="' + product.id + '" data-rebuy-sku="' + esc(data.sku) + '">' + esc(t('quickAdd')) + '</button>';
            } else if (isLoggedIn) {
              buttonHtml = '<a href="' + data.productUrl + '" class="btn btn--primary related_products__buy_buttons">' + esc(buttonText) + '</a>';
            } else {
              buttonHtml = '<a href="' + data.productUrl + '" class="btn btn--primary related_products__buy_buttons">' + esc(buyOnlineLabel) + '</a>';
            }

            return (
              '<div class="related_product__item_wrapper" data-width="' + (carouselElement.dataset.cardWidth || '90') + '">' +
                '<div class="card-background" data-card-bg="' + esc(cardBackground) + '" data-card-border="">' +
                  '<a href="' + data.productUrl + '" class="related_product__image image-medium">' +
                    '<div class="image-container">' +
                      primaryImageHtml +
                      (hoverSrc ? '<img src="' + hoverSrc + '" alt="' + alt + '" class="hover-image" loading="lazy" width="200" height="200">' : '') +
                    '</div>' +
                  '</a>' +
                  '<div class="related_product__info_wrapper flex flex-col">' +
                    (showVendor && product.vendor ? '<div class="related_product__vendor">' + esc(product.vendor) + '</div>' : '') +
                    '<a href="' + data.productUrl + '" class="related_product__title font-bold">' + esc(product.title || '') + '</a>' +
                    '<div class="related_product__price flex flex-col">' +
                      rrpPrice(data) +
                      tradePrice(product) +
                    '</div>' +
                    (showCardButton ? buttonHtml : '') +
                  '</div>' +
                '</div>' +
              '</div>'
            );
          }).join('');

          // Concept 1: quick-add list. Figma shows two rows then a disclosure.
          const LIST_VISIBLE = 2;
          const listHTML =
            '<div class="rebuy-layout rebuy-layout--list">' +
              '<p class="rebuy-layout__lede">' + esc(t('listLede')) + '</p>' +
              (function () {
                const row = function (product, index) {
                  const data = svc.extractProductData(product);
                  const stagger = index >= LIST_VISIBLE ? ' style="--rebuy-stagger:' + ((index - LIST_VISIBLE) * 90) + 'ms"' : '';
                  return '<div class="rebuy-row"' + stagger + '>' +
                    buildMedia(product, data, 144) +
                    '<span class="rebuy-row__body">' +
                      '<a class="rebuy-row__title" href="' + data.productUrl + '">' + esc(product.title || '') + '</a>' +
                      tradePrice(product) + rrpPrice(data) +
                    '</span>' +
                    '<span class="rebuy-row__action">' + buildButton(product, data) + '</span>' +
                  '</div>';
                };
                const shown = products.slice(0, LIST_VISIBLE).map(row).join('');
                const rest = products.slice(LIST_VISIBLE);
                if (!rest.length) return shown;
                const panelId = 'rebuy-list-extra-' + esc(attributionId).replace(/[^a-z0-9-]/gi, '') + '-' + LIST_VISIBLE;
                return shown +
                  '<div class="rebuy-list__extra" id="' + panelId + '">' +
                    '<div class="rebuy-list__extra-inner">' +
                      rest.map(function (product, i) { return row(product, i + LIST_VISIBLE); }).join('') +
                    '</div>' +
                  '</div>' +
                  '<button type="button" class="rebuy-list__more" aria-expanded="false" aria-controls="' + panelId + '">' +
                    esc(rest.length === 1 ? t('showMoreOne') : t('showMoreOther', { count: rest.length })) +
                  '</button>';
              })() +
            '</div>';

          // Concept 2: multi-select with a running add-on total and one combined action.
          const bundleHTML =
            '<div class="rebuy-layout rebuy-layout--bundle">' +
              '<p class="rebuy-layout__lede">' + esc(t('bundleLede')) + '</p>' +
              '<fieldset class="rebuy-bundle__set">' +
                '<legend class="visually-hidden">Optional accessories</legend>' +
                products.map(function (product, index) {
                  const data = svc.extractProductData(product);
                  const id = 'rebuy-bundle-' + product.id + '-' + index;
                  const price = data.variant && data.variant.price ? parseFloat(data.variant.price) : 0;
                  return '<div class="rebuy-bundle__row">' +
                    '<input type="checkbox" class="rebuy-bundle__check" id="' + id + '"' +
                      ' data-price="' + price + '"' +
                      ' data-rebuy-attribution="' + esc(attributionId) + '"' +
                      ' data-rebuy-product-id="' + product.id + '"' +
                      ' data-rebuy-sku="' + esc(data.sku) + '">' +
                    '<label class="rebuy-bundle__label" for="' + id + '">' +
                      '<span class="rebuy-bundle__box" aria-hidden="true"></span>' +
                      buildMedia(product, data, 128) +
                      '<span class="rebuy-bundle__text">' +
                        '<span class="rebuy-row__title">' + esc(product.title || '') + '</span>' +
                        tradePrice(product) + rrpPrice(data) +
                      '</span>' +
                    '</label>' +
                  '</div>';
                }).join('') +
              '</fieldset>' +
              '<div class="rebuy-bundle__summary" aria-live="polite">' +
                '<div class="rebuy-bundle__line">' +
                  '<span class="rebuy-bundle__count">0 accessories selected</span>' +
                  '<span class="rebuy-bundle__label-total">' + esc(t('bundleTotalLabel')) + '</span>' +
                '</div>' +
                '<div class="rebuy-bundle__line">' +
                  '<span class="rebuy-bundle__note">' + esc(t('bundleNote')) + '</span>' +
                  '<span class="rebuy-bundle__total" data-rebuy-bundle-total></span>' +
                '</div>' +
              '</div>' +
              '<button type="button" class="btn btn--primary rebuy-bundle__cta" disabled>' + esc(t('addToCart')) + '</button>' +
              '<p class="rebuy-layout__foot">' + esc(t('bundleFoot')) + '</p>' +
            '</div>';

          // The theme's caret icons already sit in the (hidden) carousel chrome, so reuse them
          // rather than text glyphs, whose font metrics never centre reliably in a circle.
          const caret = function (dir) {
            const el = carouselElement.querySelector('.swiper-button-' + dir);
            return el ? el.innerHTML.trim() : (dir === 'prev' ? '&#8249;' : '&#8250;');
          };

          // Concept 3: one accessory at a time, with more room for imagery.
          const singleHTML =
            '<div class="rebuy-layout rebuy-layout--single">' +
              '<p class="rebuy-layout__lede">' + esc(t('singleLede')) + '</p>' +
              products.map(function (product, index) {
                const data = svc.extractProductData(product);
                return '<div class="rebuy-single__slide" data-index="' + index + '"' + (index ? ' hidden' : '') + '>' +
                  '<a class="rebuy-single__media" href="' + data.productUrl + '">' + (data.primaryImage
                    ? '<img src="' + svc.getImageUrl(data.primaryImage, 252) + '" alt="' + esc(product.title || '') + '" loading="lazy">'
                    : (placeholderImage ? '<img src="' + placeholderImage + '" alt="" loading="lazy">' : placeholderSvg)) + '</a>' +
                  '<div class="rebuy-single__body">' +
                    '<a class="rebuy-single__title" href="' + data.productUrl + '">' + esc(product.title || '') + '</a>' +
                    '<span class="rebuy-single__price">' + tradePrice(product) + rrpPrice(data) + '</span>' +
                    (function () {
                      const variants = (product.variants || []).filter(function (variant) { return variant && variant.id; });
                      // Every product on this store is single-variant, so render nothing rather than
                      // a control the customer cannot use. The select below still covers the case of a
                      // genuinely multi-variant product.
                      if (variants.length < 2) return '';
                      const selectId = 'rebuy-variant-' + product.id + '-' + index;
                      return '<span class="rebuy-single__variant-wrap">' +
                        '<label class="visually-hidden" for="' + selectId + '">' + esc(t('chooseOption', { product: product.title || '' })) + '</label>' +
                        '<select class="rebuy-single__variant" id="' + selectId + '" data-rebuy-variant-select>' +
                          variants.map(function (variant) {
                            const optionLabel = variant.title && variant.title !== 'Default Title'
                              ? variant.title
                              : (variant.sku || t('optionFallback'));
                            return '<option value="' + esc(String(variant.id)) + '" data-sku="' + esc(variant.sku || '') + '"' +
                              (variant.available === false ? ' disabled' : '') + '>' + esc(optionLabel) + '</option>';
                          }).join('') +
                        '</select>' +
                      '</span>';
                    })() +
                    buildButton(product, data, 'rebuy-single__cta') +
                  '</div>' +
                '</div>';
              }).join('') +
              '<div class="rebuy-single__nav">' +
                '<button class="rebuy-single__arrow" type="button" data-dir="-1" aria-label="' + esc(t('slidePrev')) + '">' + caret('prev') + '</button>' +
                '<span class="rebuy-single__progress">' +
                  products.map(function (_, index) {
                    return '<button class="rebuy-single__dot" type="button" role="tab" data-index="' + index + '" aria-label="' + esc(t('slideLabel', { index: index + 1 })) + '" aria-selected="' + (index === 0) + '"></button>';
                  }).join('') +
                  '<span class="rebuy-single__count">' + esc(t('slideCounter', { current: 1, total: products.length })) + '</span>' +
                '</span>' +
                '<button class="rebuy-single__arrow" type="button" data-dir="1" aria-label="' + esc(t('slideNext')) + '">' + caret('next') + '</button>' +
              '</div>' +
            '</div>';

          // Concept 4, POC ONLY. Roles and their copy are hardcoded here and mapped onto the first
          // three recommendations. Production needs one Rebuy Data Source per role, role labels from
          // Shopify custom data, and every string through | t. Do not ship this mapping.
          // POC roles, supplied by Liquid. These become metaobject-driven if concept 4 ships.
          const POC_ROLES = (strings.roles && strings.roles.length) ? strings.roles : [];
          const iconTpl = carouselElement.querySelector('template[data-rebuy-role-icons]');
          // Looked up by role key rather than by array position: the roles will come from data, and
          // index coupling breaks the moment a profile defines a different set or order.
          const roleIconFor = function (roleKey) {
            if (!iconTpl) return '';
            const svg = iconTpl.content.querySelector('[data-rebuy-icon="' + roleKey + '"]');
            return svg ? svg.outerHTML : '';
          };
          const switcherProducts = products.slice(0, POC_ROLES.length);
          const switcherHTML =
            '<div class="rebuy-layout rebuy-layout--switcher">' +
              '<div class="rebuy-switcher__head">' +
                '<span class="rebuy-switcher__title">' + esc(t('switcherTitle')) + '</span>' +
                '<span class="rebuy-switcher__sub">' + esc(t('switcherSub')) + '</span>' +
              '</div>' +
              '<div class="rebuy-switcher__tabs" role="tablist">' +
                '<span class="rebuy-switcher__marker" aria-hidden="true"></span>' +
                switcherProducts.map(function (_, index) {
                  return '<button class="rebuy-switcher__tab" type="button" role="tab" data-index="' + index + '"' +
                    ' aria-selected="' + (index === 0) + '">' + roleIconFor(POC_ROLES[index].key) +
                    '<span>' + POC_ROLES[index].label + '</span></button>';
                }).join('') +
              '</div>' +
              switcherProducts.map(function (product, index) {
                const data = svc.extractProductData(product);
                return '<div class="rebuy-switcher__panel" role="tabpanel" data-index="' + index + '"' + (index ? ' hidden' : '') + '>' +
                  '<a class="rebuy-switcher__media" href="' + data.productUrl + '">' + (data.primaryImage
                    ? '<img src="' + svc.getImageUrl(data.primaryImage, 264) + '" alt="' + esc(product.title || '') + '" loading="lazy">'
                    : (placeholderImage ? '<img src="' + placeholderImage + '" alt="" loading="lazy">' : placeholderSvg)) + '</a>' +
                  '<div class="rebuy-switcher__body">' +
                    '<a class="rebuy-switcher__name" href="' + data.productUrl + '">' + esc(product.title || '') + '</a>' +
                    tradePrice(product) + rrpPrice(data) +
                    '<span class="rebuy-switcher__utility">' + POC_ROLES[index].utility + '</span>' +
                    buildButton(product, data, 'rebuy-switcher__cta') +
                  '</div>' +
                '</div>';
              }).join('') +
              '<p class="rebuy-layout__foot">' + esc(t('switcherFoot')) + '</p>' +
            '</div>';

          const byLayout = {
            carousel: cardsHTML,
            list: listHTML,
            bundle: bundleHTML,
            single: singleHTML,
            switcher: switcherHTML
          };

          carouselElement.dataset.layout = layout;
          placeholder.outerHTML = byLayout[layout] || cardsHTML;
          if (layout !== 'carousel') initLayoutBehaviour(carouselElement, layout, plainMoneyFormat, svc, t);
          bindDirectAdd(carouselElement, svc, t);
          hydrateRRP(carouselElement);
          const banner = carouselElement.querySelector('.related_products_banner');
          if (banner) banner.style.display = '';

          requestAnimationFrame(function() {
            carouselElement.dispatchEvent(new CustomEvent('recommendations-loaded'));
            products.forEach(function(product) {
              window.RebuyService.trackProductView(product);
            });
          });
        })
        .catch(function(err) {
          // Deliberate, not debug output. The block is removed on failure, so without this a render
          // error deletes the whole widget leaving nothing on screen and nothing in the console.
          console.warn('[Rebuy PDP] Failed to render recommendations:', err);
          carouselElement.remove();
        });
    }

    fetchWhenReady();
  };

  const observer = new IntersectionObserver(handleIntersection, { rootMargin: '0px 0px 200px 0px' });
  observer.observe(placeholder);
  }

  function initAll(root) {
    (root || document)
      .querySelectorAll('related-products-carousel [data-rebuy-pdp-recommendations]')
      .forEach(function (placeholder) {
        const carousel = placeholder.closest('related-products-carousel');
        if (carousel) initRebuyRecommendations(carousel);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // The theme editor re-renders sections without a page load, so re-scan the injected markup.
  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
})();
